import { z } from 'zod';
import { defineEndpoint } from '../define';
import { ContentTypeSchema } from '../device-file-groups';

export const DeviceFileSchema = z.object({
  id: z.string().uuid(),
  deviceId: z.string().uuid(),
  groupId: z.string().uuid(),
  groupSlug: z.string(),
  url: z.string().url(),
  filename: z.string(),
  contentType: ContentTypeSchema,
  sizeBytes: z.number().int(),
  sortOrder: z.number().int(),
  uploadedByUserId: z.string().uuid(),
  uploadedAt: z.string(),
});
export type DeviceFile = z.infer<typeof DeviceFileSchema>;

export const UploadUrlBodySchema = z.object({
  groupSlug: z.string(),
  contentType: ContentTypeSchema,
  filename: z.string().trim().min(1).max(255),
  sizeBytes: z
    .number()
    .int()
    .min(1)
    .max(20 * 1024 * 1024),
});
export type UploadUrlBody = z.infer<typeof UploadUrlBodySchema>;

export const UploadUrlResponseSchema = z.object({
  data: z.object({
    presignedUrl: z.string().url(),
    storagePath: z.string(),
    expiresAt: z.string(),
  }),
});

export const requestDeviceFileUploadUrl = defineEndpoint({
  method: 'POST',
  path: '/v1/devices/:deviceId/files/upload-url',
  params: z.object({ deviceId: z.string().uuid() }),
  query: z.object({}),
  body: UploadUrlBodySchema,
  response: UploadUrlResponseSchema,
});

export const FinalizeFileBodySchema = z.object({
  groupSlug: z.string(),
  storagePath: z.string(),
  contentType: ContentTypeSchema,
  filename: z.string().trim().min(1).max(255),
  sizeBytes: z
    .number()
    .int()
    .min(1)
    .max(20 * 1024 * 1024),
  sortOrder: z.number().int().optional(),
});
export type FinalizeFileBody = z.infer<typeof FinalizeFileBodySchema>;

export const FinalizeFileResponseSchema = z.object({ data: DeviceFileSchema });

export const finalizeDeviceFile = defineEndpoint({
  method: 'POST',
  path: '/v1/devices/:deviceId/files',
  params: z.object({ deviceId: z.string().uuid() }),
  query: z.object({}),
  body: FinalizeFileBodySchema,
  response: FinalizeFileResponseSchema,
  successStatus: 201,
});

export const deleteDeviceFile = defineEndpoint({
  method: 'DELETE',
  path: '/v1/devices/:deviceId/files/:fileId',
  params: z.object({ deviceId: z.string().uuid(), fileId: z.string().uuid() }),
  query: z.object({}),
  body: z.undefined(),
  response: z.object({}),
  successStatus: 204,
});

export const ReorderFileBodySchema = z.object({
  sortOrder: z.number().int().min(0),
});

export const reorderDeviceFile = defineEndpoint({
  method: 'PATCH',
  path: '/v1/devices/:deviceId/files/:fileId',
  params: z.object({ deviceId: z.string().uuid(), fileId: z.string().uuid() }),
  query: z.object({}),
  body: ReorderFileBodySchema,
  response: FinalizeFileResponseSchema,
});
